<template>
  <v-dialog
    :value="dialog"
    :max-width="modalLayout.FORM_COMPACT"
    v-bind="modalP2FormViewport"
    @input="$emit('update:dialog', $event)"
    @click:outside="close"
    @keydown.esc="close"
  >
    <v-card>
      <CrudFormDialogHeader
        :kicker="$t('user.accountInfo')"
        :title="$t('user.password.title')"
      />

      <v-card-text>
        <v-container>
          <v-form ref="form" v-model="valid">
            <v-row>
              <v-col v-if="accessType === 'me'" cols="3" class="custom-padding d-flex align-center">
                <label class="text-no-wrap">{{ $t('user.password.current') }}</label>
              </v-col>
              <v-col v-if="accessType === 'me'" cols="9" class="custom-padding">
                <v-text-field
                  v-model="currentPassword"
                  :type="showCurrentPassword ? 'text' : 'password'"
                  dense
                  :rules="[requiredRule]"
                  required
                  :append-icon="showCurrentPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  @click:append="showCurrentPassword = !showCurrentPassword"
                />
              </v-col>
              <v-col cols="3" class="custom-padding d-flex align-center">
                <label class="text-no-wrap">{{ $t('user.password.newPassword') }}</label>
              </v-col>
              <v-col cols="9" class="custom-padding">
                <v-text-field
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  dense
                  :rules="[requiredRule]"
                  required
                  :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  @click:append="showPassword = !showPassword"
                />
              </v-col>
              <v-col cols="3" class="custom-padding d-flex align-center">
                <label class="text-no-wrap">{{ $t('user.password.newPasswordConfirm') }}</label>
              </v-col>
              <v-col cols="9" class="custom-padding">
                <v-text-field
                  v-model="passwordConfirm"
                  :type="showPasswordConfirm ? 'text' : 'password'"
                  dense
                  :rules="[requiredRule, passwordConfirmRule]"
                  required
                  :append-icon="showPasswordConfirm ? 'mdi-eye' : 'mdi-eye-off'"
                  @click:append="showPasswordConfirm = !showPasswordConfirm"
                />
              </v-col>
            </v-row>
            <v-alert
              v-if="error"
              type="error"
              dense
              class="mt-2"
            >
              {{ error }}
            </v-alert>
          </v-form>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          outlined
          class="solvit-crud-outline-btn"
          :disabled="loading"
          @click="close"
        >
          {{ $t('common.cancel') }}
        </v-btn>
        <v-btn
          outlined
          class="solvit-crud-outline-btn solvit-crud-outline-btn--primary"
          :loading="loading"
          :disabled="loading"
          @click="save"
        >
          {{ $t('common.save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Api from '@/apis/Api';
import CrudFormDialogHeader from '@/components/common/CrudFormDialogHeader.vue';
import { MODAL_MAX_WIDTH, MODAL_P2_FORM_VIEWPORT } from '@/constants/modalLayout';

export default {
  name: 'ChangePasswordDialog',
  components: {
    CrudFormDialogHeader
  },
  props: {
    dialog: {
      type: Boolean,
      required: true
    },
    userSeq: {
      type: [Number, String],
      required: true
    },
    accessType: {
      type: String,
      default: 'me',
      validator: value => ['me', 'all'].includes(value)
    }
  },
  data: () => ({
    modalLayout: MODAL_MAX_WIDTH,
    modalP2FormViewport: MODAL_P2_FORM_VIEWPORT,
    password: '',
    currentPassword: '',
    valid: true,
    passwordConfirm: '',
    showPassword: false,
    showPasswordConfirm: false,
    showCurrentPassword: false,
    loading: false,
    error: null
  }),
  computed: {
    requiredRule() {
      return (v) => !!v || this.$t('common.required');
    },
    passwordConfirmRule() {
      return (v) => v === this.password || this.$t('common.passwordMismatch');
    }
  },
  methods: {
    async save() {
      if (!this.$refs.form.validate()) return;

      this.loading = true;
      this.error = null;

      try {
        const endpoint = this.accessType === 'me'
          ? `/user/${this.userSeq}/password`
          : `/users/${this.userSeq}/password`;

        const requestData = this.accessType === 'me'
          ? {
            userSeq: this.userSeq,
            currentPassword: this.currentPassword,
            newPassword: this.password
          }
          : {
            newPassword: this.password
          };

        await Api.update(endpoint, requestData);
        this.$emit('success');
        this.close();
      } catch (error) {
        this.error = error.response?.data?.message || this.$t('user.password.changeError');
      } finally {
        this.loading = false;
      }
    },
    close() {
      this.$emit('close');
      this.password = '';
      this.passwordConfirm = '';
      this.currentPassword = '';
      this.error = null;
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    }
  }
};
</script>

<style scoped>
.custom-padding {
  padding: 8px 12px !important;
}
.text-no-wrap {
  white-space: nowrap;
  min-width: fit-content;
}
</style>
